import { ReactElement, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Typography,
  SvgIcon,
  Snackbar
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import {
  createStyles,
  makeStyles,
  Theme
} from '@material-ui/core/styles'
import { useAuth } from 'ustaxes/auth/AuthContext'
import Urls from 'ustaxes/data/urls'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(3),
      backgroundColor: '#F9FAFB'
    },
    card: {
      maxWidth: 440,
      width: '100%',
      borderRadius: 16,
      boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      border: '1px solid #E5E7EB',
      backgroundColor: '#FFFFFF'
    },
    cardContent: {
      padding: theme.spacing(5, 4)
    },
    logoSection: {
      textAlign: 'center',
      marginBottom: theme.spacing(3)
    },
    flagEmoji: {
      fontSize: 48,
      display: 'block',
      marginBottom: theme.spacing(1.5)
    },
    logoText: {
      fontWeight: 800,
      fontSize: '1.5rem',
      color: '#1A237E'
    },
    subtitle: {
      color: '#3949AB',
      fontSize: '0.9rem',
      marginTop: theme.spacing(0.5)
    },
    oauthButton: {
      padding: '12px 20px',
      borderRadius: 10,
      fontSize: '0.95rem',
      fontWeight: 600,
      textTransform: 'none',
      justifyContent: 'center',
      gap: theme.spacing(1.5),
      border: '1px solid #E5E7EB',
      backgroundColor: '#FFFFFF',
      color: '#1A237E',
      '&:hover': {
        backgroundColor: '#F9FAFB',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }
    },
    googleButton: {
      marginBottom: theme.spacing(1.5)
    },
    microsoftButton: {
      marginBottom: theme.spacing(2.5)
    },
    divider: {
      margin: theme.spacing(2.5, 0)
    },
    dividerText: {
      color: '#9CA3AF',
      fontSize: '0.8rem',
      padding: theme.spacing(0, 2),
      backgroundColor: '#FFFFFF'
    },
    guestButton: {
      borderRadius: 10,
      padding: '12px 20px',
      fontSize: '0.95rem',
      fontWeight: 600
    },
    footer: {
      textAlign: 'center',
      marginTop: theme.spacing(3),
      color: '#9CA3AF',
      fontSize: '0.78rem',
      lineHeight: 1.5
    },
    priceNote: {
      textAlign: 'center',
      marginTop: theme.spacing(2),
      padding: theme.spacing(1.5, 2),
      borderRadius: 8,
      backgroundColor: '#F9FAFB',
      border: '1px solid #E5E7EB'
    }
  })
)

// Google "G" logo
const GoogleIcon = (): ReactElement => (
  <SvgIcon viewBox="0 0 24 24" style={{ fontSize: 20 }}>
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </SvgIcon>
)

// Microsoft logo
const MicrosoftIcon = (): ReactElement => (
  <SvgIcon viewBox="0 0 24 24" style={{ fontSize: 20 }}>
    <path d="M1 1h10v10H1z" fill="#F25022" />
    <path d="M13 1h10v10H13z" fill="#7FBA00" />
    <path d="M1 13h10v10H1z" fill="#00A4EF" />
    <path d="M13 13h10v10H13z" fill="#FFB900" />
  </SvgIcon>
)

const LoginPage = (): ReactElement => {
  const classes = useStyles()
  const { signInWithGoogle, signInWithMicrosoft } = useAuth()
  const [showNotice, setShowNotice] = useState(false)

  const isOAuthConfigured =
    Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID) &&
    Boolean(process.env.REACT_APP_MICROSOFT_CLIENT_ID)

  const handleOAuth = (provider: 'google' | 'microsoft') => {
    if (!isOAuthConfigured) {
      setShowNotice(true)
      return
    }
    if (provider === 'google') {
      signInWithGoogle()
    } else {
      signInWithMicrosoft()
    }
  }

  return (
    <Box className={classes.root}>
      <Helmet>
        <title>Sign In - Ameritax</title>
      </Helmet>

      <Card className={classes.card} elevation={0}>
        <CardContent className={classes.cardContent}>
          {/* Logo */}
          <Box className={classes.logoSection}>
            <span className={classes.flagEmoji} role="img" aria-label="US flag">
              {'\u{1F1FA}\u{1F1F8}'}
            </span>
            <Typography className={classes.logoText}>Ameritax</Typography>
            <Typography className={classes.subtitle}>
              Sign in to file your tax return
            </Typography>
          </Box>

          {/* OAuth Buttons */}
          <Button
            fullWidth
            variant="outlined"
            className={`${classes.oauthButton} ${classes.googleButton}`}
            onClick={() => handleOAuth('google')}
            startIcon={<GoogleIcon />}
          >
            Continue with Google
          </Button>

          <Button
            fullWidth
            variant="outlined"
            className={`${classes.oauthButton} ${classes.microsoftButton}`}
            onClick={() => handleOAuth('microsoft')}
            startIcon={<MicrosoftIcon />}
          >
            Continue with Microsoft
          </Button>

          {/* Divider */}
          <Box display="flex" alignItems="center" className={classes.divider}>
            <Divider style={{ flex: 1 }} />
            <Typography className={classes.dividerText}>or</Typography>
            <Divider style={{ flex: 1 }} />
          </Box>

          {/* Guest / Start without account */}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            className={classes.guestButton}
            component={RouterLink}
            to={Urls.taxPayer.info}
          >
            Continue as Guest
          </Button>

          {/* Price note */}
          <Box className={classes.priceNote}>
            <Typography variant="caption" style={{ color: '#3949AB' }}>
              <strong>$20</strong> per return &mdash; Federal + State included.
              <br />
              Start for free, pay only when you file.
            </Typography>
          </Box>

          {/* Footer */}
          <Typography className={classes.footer}>
            By signing in, you agree to Ameritax&apos;s Terms of Service
            <br />
            and Privacy Policy. Your data is encrypted and secure.
          </Typography>
        </CardContent>
      </Card>

      <Snackbar
        open={showNotice}
        autoHideDuration={5000}
        onClose={() => setShowNotice(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowNotice(false)}
          severity="info"
          variant="filled"
        >
          OAuth login requires server configuration. Use &quot;Continue as
          Guest&quot; for now.
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default LoginPage
